import * as v from "valibot";
import { apiConfig } from "./api-config";
import { createAuthAwareFetch } from "./auth-fetch";
import { authClientIPHeaderNames, isAuthRateLimitedRequest } from "./auth-client-ip";

// NOIMAN APIの型定義
export interface NOIMANCourse {
  id: string;
  title: string;
  slug: string;
  description: string;
  category: string;
  level: string;
  hasFreeTrial: boolean;
  instructorId?: string;
  thumbnailUrl?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NOIMANCourseDetail extends NOIMANCourse {
  sections: Array<{
    id: string;
    title: string;
    description: string;
    orderIndex: number;
    lessons: Array<{
      id: string;
      title: string;
      description: string;
      duration: number;
      orderIndex: number;
      isFree: boolean;
      type: string;
    }>;
  }>;
  lessonCount: number;
  freeLessonCount: number;
  estimatedDuration: string;
}

export interface NOIMANCourseList {
  courses: NOIMANCourse[];
  count: number;
}

export interface NOIMANUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "instructor" | "student";
}

export interface NOIMANCourseEnrollment {
  id: string;
  userId: string;
  courseId: string;
  enrolledAt: string;
  completedAt?: string;
  progress: number;
  lastAccessedAt: string;
  isActive: boolean;
  metadata?: any;
}

export interface NOIMANLessonProgress {
  id: string;
  userId: string;
  lessonId: string;
  courseId: string;
  status: "not_started" | "in_progress" | "completed";
  startedAt?: string;
  completedAt?: string;
  timeSpent: number;
  score?: number;
  attempts: number;
  lastAccessedAt: string;
  metadata?: any;
}

// APIエンドポイントの型定義
export type NOIMANApiEndpoints = {
  "/api/courses/v1/getCourseList": {
    request: undefined;
    response: NOIMANCourseList;
  };
  "/api/courses/v1/getCourseDetail": {
    request: { slug: string };
    response: NOIMANCourseDetail;
  };
  "/api/users/v1/getProfile": {
    request: undefined;
    response: { success: boolean; data: any };
  };
  "/api/users/v1/getEnrollments": {
    request: undefined;
    response: { success: boolean; data: NOIMANCourseEnrollment[] };
  };
  "/api/users/v1/getProgress": {
    request: { courseId?: string };
    response: { success: boolean; data: NOIMANLessonProgress[] };
  };
  "/api/auth/v1/checkAuthorization": {
    request: undefined;
    response: { user: NOIMANUser };
  };
  /*
  refreshToken は本文に来ない。HttpOnly cookie だけで渡している (api-go の
  loginHandler)。型に書いてあると「読めるもの」に見えるので消す
  */
  "/api/auth/v1/signInWithEmail": {
    request: { email: string; password: string };
    response: {
      user: NOIMANUser;
      accessToken: string;
    };
  };
  "/api/auth/v1/me": {
    request: undefined;
    response: { user: NOIMANUser };
  };
  "/quiz/v1/getQuestions": {
    request: { courseId: string; lessonId: string };
    response: {
      questions: Array<{
        id: string;
        questionText: string;
        questionHtml: string;
        explanation: string;
        explanationHtml: string;
        difficulty: string;
        points: number;
        timeLimit: number;
        choices: Array<{
          id: string;
          choiceText: string;
          choiceHtml: string;
          isCorrect: boolean;
          explanation: string;
        }>;
      }>;
      total: number;
    };
  };
  "/api/quiz/v1/getUserAnswers/:courseId/:lessonId": {
    request: undefined;
    response: {
      answers: Array<{
        questionId: string;
        selectedChoiceId?: string;
        answerText?: string;
        isCorrect: boolean;
        answeredAt: string;
      }>;
      hasCompletedQuiz: boolean;
    };
  };
};

// Result型の定義
export type Result<T, E> = { isOk: true; data: T } | { isOk: false; error: E };

// APIエラーの定義
export class NOIMANApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: any
  ) {
    super(message);
    this.name = "NOIMANApiError";
  }
}

// 成功時のResultを作成
function ok<T>(data: T): Result<T, never> {
  return { isOk: true, data };
}

// エラー時のResultを作成
function err<E>(error: E): Result<never, E> {
  return { isOk: false, error };
}

// 非同期処理をResult型でラップ（NOIMANApiError用）
export async function tryCatchAsyncNOIMAN<T>(
  fn: () => Promise<T>
): Promise<Result<T, NOIMANApiError>> {
  try {
    const result = await fn();
    return ok(result);
  } catch (error) {
    if (error instanceof NOIMANApiError) {
      return err(error);
    }
    return err(
      new NOIMANApiError(
        error instanceof Error ? error.message : String(error),
        500
      )
    );
  }
}

// APIリクエストの基本関数
export async function apiRequest<T extends keyof NOIMANApiEndpoints>(
  endpoint: T,
  request?: NOIMANApiEndpoints[T]["request"],
  baseURL = apiConfig.getApiUrl(),
  onAuthFailed?: () => void,
  method?: "GET" | "POST" | "PUT" | "DELETE",
  context?: any // SSRコンテキスト（Cookieヘッダーを含む）
): Promise<Result<NOIMANApiEndpoints[T]["response"], NOIMANApiError>> {
  return tryCatchAsyncNOIMAN(async () => {
    let url = `${baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // SSR時のCookieヘッダー転送
    // contextが存在し、requestオブジェクトを持つ場合はCookieヘッダーを転送
    if (context?.request?.headers?.get) {
      const cookieHeader = context.request.headers.get("Cookie");
      if (cookieHeader) {
        headers["Cookie"] = cookieHeader;
      }
    }

    // GETリクエストの場合はクエリパラメータとして追加
    let body: string | undefined;
    const requestMethod = method || (request ? "POST" : "GET");
    if (
      isAuthRateLimitedRequest(requestMethod, endpoint) &&
      context?.authClientIPHeaders instanceof Headers
    ) {
      for (const name of authClientIPHeaderNames) {
        const value = context.authClientIPHeaders.get(name);
        if (value) headers[name] = value;
      }
    }

    if (requestMethod === "GET" && request) {
      const params = new URLSearchParams();
      Object.entries(request).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
      url += `?${params.toString()}`;
    } else if (request) {
      body = JSON.stringify(request);
    }

    // APIリクエストを送信（Cookieを含める）
    // アクセストークン失効時は自動でリフレッシュして1回だけリトライする
    const authAwareFetch = createAuthAwareFetch(context?.request);
    const response = await authAwareFetch(url, {
      method: requestMethod,
      headers,
      body,
      credentials: "include", // Cookieを送信するために必要
    });

    // エラーハンドリング
    if (!response.ok) {
      if (response.status === 401) {
        onAuthFailed?.();
      }

      throw new NOIMANApiError(
        response.statusText,
        response.status,
        await response.text().catch(() => undefined)
      );
    }

    // レスポンスデータをパース
    const data =
      (await response.json()) as NOIMANApiEndpoints[T]["response"];

    // 成功フラグのチェック
    if (
      data &&
      typeof data === "object" &&
      "success" in data &&
      data.success === false
    ) {
      throw new NOIMANApiError(
        (data as any).error || "API request failed",
        response.status,
        data
      );
    }

    return data;
  });
}

// 認証付きAPIリクエスト
export async function authenticatedApiRequest<
  T extends keyof NOIMANApiEndpoints,
>(
  endpoint: T,
  accessToken: string,
  request?: NOIMANApiEndpoints[T]["request"],
  baseURL = apiConfig.getApiUrl()
): Promise<Result<NOIMANApiEndpoints[T]["response"], NOIMANApiError>> {
  return tryCatchAsyncNOIMAN(async () => {
    const url = `${baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };

    // リクエストボディの構築
    let body: string | undefined;
    if (request) {
      body = JSON.stringify(request);
    }

    // APIリクエストを送信
    const response = await fetch(url, {
      method: request ? "POST" : "GET",
      headers,
      body,
    });

    // エラーハンドリング
    if (!response.ok) {
      throw new NOIMANApiError(
        response.statusText,
        response.status,
        await response.text().catch(() => undefined)
      );
    }

    // レスポンスデータをパース
    const data =
      (await response.json()) as NOIMANApiEndpoints[T]["response"];

    // 成功フラグのチェック
    if (
      data &&
      typeof data === "object" &&
      "success" in data &&
      data.success === false
    ) {
      throw new NOIMANApiError(
        (data as any).error || "API request failed",
        response.status,
        data
      );
    }

    return data;
  });
}

// GETメソッド用の関数
async function apiProxyGet<T>(
  context: any,
  endpoint: string,
  options?: { accessToken?: string }
): Promise<{ isErr: boolean; data?: T; error?: string; status?: number }> {
  const apiUrl = apiConfig.getApiUrl();
  const result = await apiRequest(
    endpoint as any,
    undefined,
    apiUrl,
    undefined,
    "GET",
    context // contextを渡してSSR時のCookieを転送
  );

  if (result.isOk) {
    return {
      isErr: false,
      data: result.data as T,
    };
  } else {
    return {
      isErr: true,
      error: result.error.message,
      status: result.error.status,
    };
  }
}

// POSTメソッド用の関数
async function apiProxyPost<T>(
  context: any,
  endpoint: string,
  body: any,
  options?: { accessToken?: string; refreshToken?: string }
): Promise<{ isErr: boolean; data?: T; error?: string; status?: number }> {
  const apiUrl = apiConfig.getApiUrl();
  const result = await apiRequest(
    endpoint as any,
    body,
    apiUrl,
    undefined,
    "POST",
    context // contextを渡してSSR時のCookieを転送
  );

  if (result.isOk) {
    return {
      isErr: false,
      data: result.data as T,
    };
  } else {
    return {
      isErr: true,
      error: result.error.message,
      status: result.error.status,
    };
  }
}

// PUTメソッド用の関数
async function apiProxyPut<T>(
  context: any,
  endpoint: string,
  body: any,
  options?: { accessToken?: string }
): Promise<{ isErr: boolean; data?: T; error?: string; status?: number }> {
  const apiUrl = apiConfig.getApiUrl();
  const result = await apiRequest(
    endpoint as any,
    body,
    apiUrl,
    undefined,
    "PUT",
    context // contextを渡してSSR時のCookieを転送
  );

  if (result.isOk) {
    return {
      isErr: false,
      data: result.data as T,
    };
  } else {
    return {
      isErr: true,
      error: result.error.message,
      status: result.error.status,
    };
  }
}

// DELETEメソッド用の関数
async function apiProxyDelete<T>(
  context: any,
  endpoint: string,
  options?: { accessToken?: string }
): Promise<{ isErr: boolean; data?: T; error?: string; status?: number }> {
  const apiUrl = apiConfig.getApiUrl();
  const result = await apiRequest(
    endpoint as any,
    undefined,
    apiUrl,
    undefined,
    "DELETE",
    context // contextを渡してSSR時のCookieを転送
  );

  if (result.isOk) {
    return {
      isErr: false,
      data: result.data as T,
    };
  } else {
    return {
      isErr: true,
      error: result.error.message,
      status: result.error.status,
    };
  }
}

// メインのAPIオブジェクト
export const apiProxyRequest = {
  get: apiProxyGet,
  post: apiProxyPost,
  put: apiProxyPut,
  delete: apiProxyDelete,
  // 互換性のために直接呼び出しもサポート（廃止予定）
  async<T>(
    context: any,
    endpoint: string,
    body?: any,
    options?: { accessToken?: string; refreshToken?: string }
  ): Promise<{ isErr: boolean; data?: T; error?: string }> {
    // 互換性のためにbodyの有無でGET/POSTを判断
    if (body === undefined || body === null) {
      return apiProxyGet<T>(context, endpoint, options);
    } else {
      return apiProxyPost<T>(context, endpoint, body, options);
    }
  },
};
